import React, { useState } from "react";
import { Card, FormGroup, FormLabel, Collapse, Form } from "react-bootstrap";
import { filterByKeyPaths, groupListBy, sortByKeyPath } from "../util/utils";
import { ObjectSelector } from "./ObjectSelector";
import { SimpleFormField } from "./SimpleFormField";
import { DateRangePicker } from "@cloudscape-design/components";

export function GroupingDisplay(props) {
	let {
		// CSS
		toolsClassName,

		// Input
		data,              // An array of records

		// Data Manipulation Setup
		groupingOptions,   // An array of possible ways to group the records
		sortingOptions,    // An array of possible ways to sort the records
		filterKeyPaths,    // An array of fields to use when filtering by keyword

		// Display Setup
		showSearchTools,   // Whether the search tools should be displayed (changes are animated)
		headerBuilder,     // Function to display the header of a table
		rowBuilder,        // Function to display the row of a table

		// Output
		dataGroupingsRef,  // A reference so the calling function can see the result of the groupings
		processingCallback,// Gets called after we process everything

		// Date Filtering
		dateRangeOptions,
		onDateChange,

		...passableProps
	} = props;

	// Fixups for lack of data
	if(groupingOptions == null) { groupingOptions = []; }
	if(sortingOptions == null) { sortingOptions = []; }
	if(filterKeyPaths == null) { filterKeyPaths = []; }
	if(headerBuilder == null) { headerBuilder = defaultHeaderBuilder(data); }
	if(rowBuilder == null) { rowBuilder = defaultRowBuilder(data); }

	// These are the user-selected fields
	let [sortingChoice, setSortingChoice] = useState(null);
	let [groupingChoices, setGroupingChoices] = useState([null]);
	let [filterString, setFilterString] = useState(null);

	/* ***** STEP 1: Filtering ***** */
	// Reduce the rows to those matching the filter string
	let filteredData = filterByKeyPaths(data, filterString, filterKeyPaths);

	/* ***** STEP 2: Grouping ***** */
	// Convert chosen grouping options into full grouping objects
	let groupings = groupingChoices.map(x => {
		return groupingOptions.filter(gopt => gopt.name == x)[0];
	}).filter(x => x != null).concat(null);
	
	// Start out in a single group with all data
	let dataGroupings = [{
		key: "all",
		labels: [],
		data: filteredData
	}];

	// Progressively refine into groups according to selected groupings
	if(data != null) {
		let tmpGroupings = dataGroupings;
		groupings.forEach(grouping => {
			if(grouping != null) {
				let newTmpGroupings = [];
				tmpGroupings.forEach(tmpGroup => {
					let newGroupings = groupListBy(tmpGroup.data, grouping.keyPath);
					for(const grpKey in newGroupings) {
						newTmpGroupings.push({
							key: `${tmpGroup.key}/${grpKey}`,
							labels: [...tmpGroup.labels, grpKey],
							data: newGroupings[grpKey]
						});
					}
				});
				tmpGroupings = newTmpGroupings;	
			}
		});
		dataGroupings = tmpGroupings;

		/* ***** Step 3: Sorting ***** */
		// Sort each grouping by the sort criteria
		if(sortingChoice != null) {
			dataGroupings.forEach(grouping => {
				if(grouping != null) {
					grouping.data.sort(sortByKeyPath(sortingChoice.keyPath, true));
				}
			});	
		}

		// Sort the groupings themselves
		dataGroupings.sort((a, b) => {
			if(a == null) {
				return 1;
			}
			if(b == null) {
				return -1;
			}
			for(const labelIdx in a.labels) {
				let aLbl = a.labels[labelIdx].toLowerCase();
				let bLbl = b.labels[labelIdx].toLowerCase();
				if(aLbl > bLbl) {
					return 1;
				}
				if(aLbl < bLbl) {
					return -1;
				}
			}
			return 0;
		});

		// Send the data back to the calling object
		if(dataGroupingsRef != null) {
			dataGroupingsRef.current = dataGroupings;
		}
	}
	processingCallback?.(filteredData.length);

	/* ***** Controller Actions ***** */
	let groupingChanged = (grpIdx) => (newGrping) => {
		let newChoices = [].concat(groupingChoices); // Get new array
		newChoices[grpIdx] = newGrping?.name;
		newChoices = newChoices.filter(x => x != null).concat(null);  // Only one null - should be last
		setGroupingChoices(newChoices)	
	};

	/* ***** Display ***** */

	// Don't show search tools if there are no search options (even if set)
	if(groupingOptions.length == 0 && sortingOptions.length == 0 && filterKeyPaths.length == 0) {
		showSearchTools = false;
	}

	return <div {...passableProps}>
		<Collapse in={showSearchTools}>
			<div className={toolsClassName}>
				<FormGroup className={`mt-4 ${groupingOptions.length == 0 ? "d-none" : ""}`}>
					<FormLabel>Grouping</FormLabel>
					{groupings.map((x, idx) =>
						<ObjectSelector className="mb-2" key={idx} options={groupingOptions} onObjectChange={groupingChanged(idx)} valueAttribute="name" labelAttribute="label" defaultObject={x} />
					)}
				</FormGroup>
				<FormGroup className={`mt-4 ${sortingOptions.length == 0 ? "d-none" : ""}`}>
					<FormLabel>Sort</FormLabel>
					<ObjectSelector options={sortingOptions} onObjectChange={setSortingChoice} valueAttribute="name" labelAttribute="label" defaultObject={sortingChoice} />
				</FormGroup>
				<SimpleFormField label="Filter" className={`mt-4 ${filterKeyPaths.length == 0 ? "d-none" : ""}`} onChange={(evt) => {setFilterString(evt.target.value)}} />
				{dateRangeOptions && <FormGroup className={`mt-4 ${sortingOptions.length == 0 ? "d-none" : ""}`}>
					<FormLabel>{dateRangeOptions.label}</FormLabel>
					<div>
						<DateRangePicker
						onChange={({ detail }) => {detail.value ? onDateChange(detail.value?.startDate, detail.value?.endDate): onDateChange("", "")}}
						value={{type: "absolute", startDate: dateRangeOptions.start, endDate: dateRangeOptions.end}}
						isValidRange={range => {
							if (range.type === "absolute") {
								const [
									startDateWithoutTime
								] = range.startDate.split("T");
								const [
									endDateWithoutTime
								] = range.endDate.split("T");
								if (
									!startDateWithoutTime ||
									!endDateWithoutTime
								) {
									return {
										valid: false,
										errorMessage:
										"The selected date range is incomplete. Select a start and end date for the date range."
									};
								}
								if (
									new Date(range.startDate) -
									new Date(range.endDate) >
									0
								) {
									return {
										valid: false,
										errorMessage:
										"The selected date range is invalid. The start date must be before the end date."
									};
								}
							}
							return { valid: true };
						}}
						i18nStrings={{applyButtonLabel: "Apply", cancelButtonLabel: "Cancel", clearButtonLabel: "Clear"}}
						placeholder="Filter by date"
						dateOnly={true}
						rangeSelectorMode="absolute-only"
						/>

					</div>
				</FormGroup>}
			</div>
		</Collapse>
		{data != null && <React.Fragment>
			<hr />
			{dataGroupings.map(dg => <GroupingTable key={dg.key} className="assetGroup mb-4" groupData={dg} groupings={groupings} groupingOptions={groupingOptions} headerBuilder={headerBuilder} rowBuilder={rowBuilder} />)}
		</React.Fragment>}
	</div>
}

function GroupingTable(props) {
	let {groupData, groupings, headerBuilder, rowBuilder, groupingOptions, ...passableProps } = props;
	let [collapsed, setCollapsed] = useState(true);

	// Build the table
	let assetTableData = <div className="table-responsive"><table className="table">
		<thead>
			{headerBuilder()}
		</thead>
		<tbody>
			{groupData.data.map(row => rowBuilder(groupData, row))}
		</tbody>
	</table></div>;

	// Stick the table in a Card if there are any groupings set
	if(groupData.labels.length > 0) {
		return <Card {...passableProps}>
			<Card.Header onClick={() => {setCollapsed(!collapsed)}}>
				<Card.Title>{groupData.labels.join(" / ")} ({groupData.data.length})</Card.Title>
			</Card.Header>
			<Card.Body className={`${collapsed ? "collapse" : ""}`}>
				{assetTableData}
			</Card.Body>
		</Card>;
	} else {
		return <div {...passableProps}>
			{assetTableData}
		</div>;
	}
}

/* These two helper functions are mostly just for testing */

function defaultHeaderBuilder(exampleData) {
	let exampleRecord = (exampleData || [])[0] || {};
	let headers = Object.keys(exampleRecord);
	let builder = (grp) => {
		return <tr>{headers.map(hdr => <th key={hdr}>{hdr}</th>)}</tr>
	};
	return builder;
}

function defaultRowBuilder(exampleData) {
	let exampleRecord = (exampleData || [])[0] || {};
	let headers = Object.keys(exampleRecord);
	let builder = (grp, row) => {
		return <tr key={JSON.stringify(row)}>{headers.map(hdr => <td key={hdr}>{JSON.stringify(row[hdr])}</td>)}</tr>
	};
	return builder;
}